// Deterministic abstract cover ("Aurora mesh") used when a blog post has no
// coverImageUrl. Same slug -> same image; colours follow the post category
// (PROBLEM_TAG). Renders a 16:9 SVG that fills its container.
import { isProblemTag } from "@/lib/matching/taxonomy";
import type { MatchingProblemTag } from "@/types/matching";

// xfnv1a string hash -> 32-bit seed
function hashSeed(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  return h >>> 0;
}

// mulberry32 PRNG
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// [deep, mid, bright, soft-light] per category — calm, on-brand.
const PALETTES: Record<MatchingProblemTag, string[]> = {
  anxiety: ["#1f3a5f", "#3d6e8f", "#7fb5c4", "#e7f2f0"],
  depression: ["#2b2d57", "#4a5391", "#8a8fd0", "#ecebf7"],
  burnout: ["#7a2e16", "#c2562b", "#e89a4f", "#fbe6d4"],
  trauma: ["#3a1f3d", "#6e3f6b", "#b07aa8", "#f3e6f0"],
  relationships: ["#7a1f3d", "#c24a6b", "#f08aa0", "#fce4ea"],
  family: ["#1f4d3a", "#3f8a63", "#86c79d", "#e4f4ea"],
  self_esteem: ["#7a5a16", "#c29a2b", "#e8cf6a", "#fbf4d8"],
  grief: ["#2e3a40", "#54686f", "#9bb0b6", "#eaf0f1"],
  parenting: ["#16607a", "#2b9bbf", "#6fd0e0", "#dcf3f7"],
  addiction: ["#3d2b16", "#7a5a2b", "#bf9a5a", "#f4ecd8"],
  sexual_problems: ["#5c1f33", "#a3415f", "#d98aa0", "#f8e6ec"],
  gender_identity: ["#3b2a5c", "#6b52a3", "#a99ad9", "#efeaf8"],
  infidelity: ["#5a2a1f", "#9a4b3a", "#d4917f", "#f7e7e2"],
  friendships: ["#1f4d45", "#3a8a7c", "#86c7b9", "#e3f4f0"],
  communication: ["#1f3f4d", "#3a7189", "#86b4c7", "#e3eff4"],
  uncertainty: ["#2c3e50", "#5a7184", "#9fb4c2", "#eef3f6"],
};

interface GeneratedCoverProps {
  slug: string;
  category?: string | null;
  className?: string;
}

export function GeneratedCover({ slug, category, className }: GeneratedCoverProps) {
  const tag: MatchingProblemTag = isProblemTag(category) ? category : "uncertainty";
  const p = PALETTES[tag];
  const rand = mulberry32(hashSeed(slug || tag));
  const id = `gc-${hashSeed(slug || tag)}`;
  const blobs = Array.from({ length: 4 }, () => ({
    cx: 150 + rand() * 1300,
    cy: 120 + rand() * 660,
    r: 380 + rand() * 340,
    fill: [p[1], p[2], p[2], p[3]][Math.floor(rand() * 4)],
    o: 0.45 + rand() * 0.4,
  }));
  return (
    <svg
      viewBox="0 0 1600 900"
      className={className}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <filter id={`${id}-b`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="120" />
        </filter>
      </defs>
      <rect width="1600" height="900" fill={p[0]} />
      <g filter={`url(#${id}-b)`}>
        {blobs.map((b, i) => (
          <circle key={i} cx={b.cx} cy={b.cy} r={b.r} fill={b.fill} opacity={b.o} />
        ))}
      </g>
    </svg>
  );
}
